/**
 * useCreateProject — TanStack Query mutation for `POST /api/projects`.
 *
 * The BE returns the new wrapped envelope `{ data: ProjectDto }` (BE-2's
 * response-wrap migration). This hook unwraps `data` on the way out so
 * callers receive a flat `Project`. On success we invalidate
 * `['projects']` so the home page picks up the freshly-created project
 * before it navigates into the new board.
 *
 * Errors are NOT toasted here — the create-project modal renders the
 * server message inline (form-level error) and decides whether to also
 * surface a toast. Centralizing that in the hook would couple the
 * mutation to a UI surface it doesn't own.
 *
 * The request/response types are hook-local (not in `src/api/types.ts`)
 * because Phase 2c codegen will replace them. Defining them inline keeps
 * the migration scoped to this file.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type { Project } from '@/api/types-bridge'
import { PROJECTS_QUERY_KEY } from '@/features/projects/use-projects'

interface CreateProjectRequest {
  name: string
  description?: string
  /** MOTE-143 — optional client this project bills against. */
  clientId?: string | null
}

interface CreateProjectResponse {
  data: Project
}

export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation<Project, Error, CreateProjectRequest>({
    mutationFn: async (input) => {
      const res = await apiClient<CreateProjectResponse>('/api/projects', {
        method: 'POST',
        body: input,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY })
    },
  })
}

export type { CreateProjectRequest, CreateProjectResponse }
